import {
  CheckCircle2,
  ArrowLeft,
  AlertTriangle,
  FileText,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { CRAComplianceQuestion, CRAComplianceAnswer } from "../types";

interface CRAComplianceFeedbackProps {
  productId: string;
  productName: string;
  answers: CRAComplianceAnswer[];
  questions: CRAComplianceQuestion[];
}

export default function CRAComplianceFeedback({
  productId,
  productName,
  answers,
  questions,
}: CRAComplianceFeedbackProps) {
  const navigate = useNavigate();
  const [expandedRef, setExpandedRef] = useState<string | null>(null);

  // Build a lookup map
  const answerMap: Record<string, CRAComplianceAnswer> = {};
  answers.forEach((a) => {
    answerMap[a.questionRef] = a;
  });

  // Collect all answerable refs (main questions without sub-questions + sub-questions)
  const allRefs: string[] = [];
  questions.forEach((q) => {
    if (q.subQuestions.length === 0) {
      allRefs.push(q.ref);
    } else {
      q.subQuestions.forEach((sq) => allRefs.push(sq.ref));
    }
  });

  const totalAnswered = allRefs.filter((ref) => answerMap[ref]?.answer).length;
  const yesCount = allRefs.filter(
    (ref) => answerMap[ref]?.answer === "Yes",
  ).length;
  const noCount = allRefs.filter(
    (ref) => answerMap[ref]?.answer === "No",
  ).length;
  const naCount = allRefs.filter(
    (ref) =>
      answerMap[ref]?.answer &&
      answerMap[ref]?.answer !== "Yes" &&
      answerMap[ref]?.answer !== "No",
  ).length;
  const completionPct =
    allRefs.length > 0 ? Math.round((yesCount / allRefs.length) * 100) : 0;

  return (
    <div className="flex-1 overflow-y-auto mt-3 space-y-4">
      {/* Score Card */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div
            className={`w-16 h-16 rounded-xl flex items-center justify-center shadow-lg ${
              completionPct >= 80
                ? "bg-green-600 shadow-green-500/25"
                : completionPct >= 50
                  ? "bg-yellow-500 shadow-yellow-500/25"
                  : "bg-red-500 shadow-red-500/25"
            }`}
          >
            <span className="text-2xl font-extrabold text-white">
              {completionPct}%
            </span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              CRA Compliance Assessment Complete
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {productName}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-2xl font-bold text-green-700 dark:text-green-300">
              {yesCount}
            </div>
            <div className="text-xs text-green-600 dark:text-green-400 font-medium">
              Yes
            </div>
          </div>
          <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <div className="text-2xl font-bold text-red-700 dark:text-red-300">
              {noCount}
            </div>
            <div className="text-xs text-red-600 dark:text-red-400 font-medium">
              No / Action needed
            </div>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
              {naCount}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">
              N/A
            </div>
          </div>
        </div>

        {noCount > 0 && (
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/40 rounded-lg flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700 dark:text-amber-400">
              {noCount} area{noCount > 1 ? "s" : ""} require
              {noCount === 1 ? "s" : ""} attention. Review the details below for
              items answered "No".
            </p>
          </div>
        )}
      </div>

      {/* Question-by-question breakdown */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <FileText className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Response Details ({totalAnswered}/{allRefs.length} answered)
          </h3>
        </div>

        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {questions.map((q) => {
            const isExpanded = expandedRef === q.ref;
            const hasSubQ = q.subQuestions.length > 0;

            // For items without sub-questions, show their direct answer
            const directAnswer = answerMap[q.ref];

            return (
              <div key={q.ref}>
                <button
                  onClick={() => setExpandedRef(isExpanded ? null : q.ref)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  {/* Status indicator */}
                  {hasSubQ ? (
                    <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400">
                        {q.subQuestions.length}
                      </span>
                    </div>
                  ) : directAnswer?.answer === "Yes" ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  ) : directAnswer?.answer === "No" ? (
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 flex-shrink-0" />
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {q.ref}. {q.name}
                    </div>
                  </div>

                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 pl-12 space-y-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {q.question}
                    </p>

                    {!hasSubQ && directAnswer && (
                      <div className="space-y-2">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            directAnswer.answer === "Yes"
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                              : directAnswer.answer === "No"
                                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                          }`}
                        >
                          {directAnswer.answer}
                        </span>
                        {directAnswer.details && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                            {directAnswer.details}
                          </p>
                        )}
                      </div>
                    )}

                    {hasSubQ &&
                      q.subQuestions.map((sq) => {
                        const sqAnswer = answerMap[sq.ref];
                        return (
                          <div
                            key={sq.ref}
                            className="border border-gray-100 dark:border-gray-700 rounded-lg p-3"
                          >
                            <div className="flex items-start gap-2">
                              {sqAnswer?.answer === "Yes" ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                              ) : sqAnswer?.answer === "No" ? (
                                <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                              ) : (
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 flex-shrink-0 mt-0.5" />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-gray-800 dark:text-gray-200">
                                  {sq.ref}. {sq.name || ""}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {sq.question}
                                </p>
                                {sqAnswer && (
                                  <div className="mt-1.5">
                                    <span
                                      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                        sqAnswer.answer === "Yes"
                                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                          : sqAnswer.answer === "No"
                                            ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                            : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                                      }`}
                                    >
                                      {sqAnswer.answer}
                                    </span>
                                    {sqAnswer.details && (
                                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 bg-gray-50 dark:bg-gray-800 p-1.5 rounded">
                                        {sqAnswer.details}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Back button */}
      <div className="flex justify-center pb-4">
        <button
          onClick={() => navigate(`/products/${productId}`)}
          className="btn btn-outline text-sm"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Product
        </button>
      </div>
    </div>
  );
}
